import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import TopNav from "../components/TopNav.jsx";

export default function OpenGatePage() {
  const [data, setData] = useState(null);
  const [flags, setFlags] = useState({ fund_the_gate_visible: false });
  const [email, setEmail] = useState("");
  const [payout, setPayout] = useState("PayPal");
  const [loading, setLoading] = useState(false);
  const [fundEmail, setFundEmail] = useState("");
  const [fundAmount, setFundAmount] = useState("");
  const [fundLoading, setFundLoading] = useState(false);

  useEffect(() => {
    api.openGate().then(setData).catch(() => {});
    api.flags().then(setFlags).catch(() => {});
  }, []);

  const signup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.openGateSignup(email, payout);
      toast.success(res.message);
      setEmail("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-4">The Open Gate</div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter uppercase text-sage-text mb-6">
          You do not have to prove anything to us.
        </h1>
        <p className="text-sage-muted text-base leading-relaxed max-w-2xl mb-4">
          We take you at your word.
        </p>
        <p className="text-sage-muted text-base leading-relaxed max-w-2xl">
          If you're experiencing homelessness, live with a disability that keeps you from traditional work, or are in active crisis — medical, disaster, displacement, escape from harm — we pay <span className="text-sage-accent">you</span> to talk with Sage. Not the other way around.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 border border-sage-border divide-x divide-sage-border my-10">
          <div className="p-4">
            <div className="text-[10px] uppercase text-sage-faint">Rate</div>
            <div className="text-2xl font-bold tabular-nums text-sage-accent">${data?.rate_per_hour_usd ?? 1}/hr</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase text-sage-faint">Extreme rate</div>
            <div className="text-2xl font-bold tabular-nums text-sage-accent">${data?.extreme_rate_usd ?? 3}/hr</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase text-sage-faint">Soft cap</div>
            <div className="text-2xl font-bold tabular-nums text-sage-accent">{data?.soft_cap_hours_per_day ?? 6}h/day</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase text-sage-faint">Payout</div>
            <div className="text-2xl font-bold tabular-nums text-sage-accent">Weekly</div>
          </div>
        </div>

        <p className="text-xs italic text-sage-muted -mt-6 mb-10" data-testid="open-gate-rate-rises">
          *This pay rate rises as the till can support it.
        </p>

        <div className="grid md:grid-cols-2 border border-sage-border divide-x divide-sage-border mb-10">
          <div className="p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-2">Hours gifted this month</div>
            <div className="text-4xl font-bold tabular-nums text-sage-accent">{data?.hours_this_month ?? "—"}</div>
            <div className="text-[10px] text-sage-faint mt-1">all-time: {data?.hours_all_time ?? "—"}</div>
          </div>
          <div className="p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-2">Coins sent this month</div>
            <div className="text-4xl font-bold tabular-nums text-sage-accent">${data?.coins_this_month ?? "—"}</div>
            <div className="text-[10px] text-sage-faint mt-1">all-time: ${data?.coins_all_time ?? "—"}</div>
          </div>
        </div>

        <form onSubmit={signup} className="border border-sage-border p-6" data-testid="open-gate-form">
          <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-2">Sign up · honor system</div>
          <p className="text-sm text-sage-muted mb-4">One person, one account. Payouts weekly by hand during beta.</p>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email"
              required
              data-testid="gate-email"
              className="flex-1 bg-sage-elevated border border-sage-border px-4 py-3 text-sage-text focus:border-sage-accent transition-colors duration-150"
            />
            <select
              value={payout}
              onChange={(e) => setPayout(e.target.value)}
              data-testid="gate-payout"
              className="bg-sage-elevated border border-sage-border px-4 py-3 text-sage-text focus:border-sage-accent"
            >
              <option>PayPal</option>
              <option>Cash App</option>
              <option>Gift card</option>
            </select>
            <button
              type="submit"
              disabled={loading}
              data-testid="gate-submit"
              className="border border-sage-accent text-sage-accent px-6 py-3 uppercase tracking-[0.1em] text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? "…" : "Walk in →"}
            </button>
          </div>
          <p className="text-[10px] text-sage-faint mt-4 uppercase tracking-[0.15em]">
            No names, faces, or stories are shared publicly. Dignity is default.
          </p>
        </form>

        {flags.fund_the_gate_visible && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!fundEmail) return;
              setFundLoading(true);
              try {
                const res = await api.gateFundInterest(fundEmail, parseFloat(fundAmount) || 0, "");
                toast.success(res.message);
                setFundEmail("");
                setFundAmount("");
              } catch (err) {
                toast.error(err.message);
              } finally {
                setFundLoading(false);
              }
            }}
            data-testid="fund-the-gate-form"
            className="border border-sage-accent p-6 mt-8 bg-sage-pink"
          >
            <div className="text-xs uppercase tracking-[0.15em] text-sage-accent mb-2">Fund the Gate</div>
            <p className="text-sm text-sage-muted mb-4">
              If you have a little more than you need this month and you want it to reach the people on the other side of this page — you can pitch in directly. Every dollar goes to Gate users, tracked on the Ledger.
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="email"
                value={fundEmail}
                onChange={(e) => setFundEmail(e.target.value)}
                placeholder="your email"
                required
                data-testid="fund-email"
                className="flex-1 bg-sage-bg border border-sage-border px-4 py-3 text-sage-text focus:border-sage-accent transition-colors duration-150"
              />
              <input
                type="number"
                min="1"
                step="1"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                placeholder="$"
                data-testid="fund-amount"
                className="w-full md:w-32 bg-sage-bg border border-sage-border px-4 py-3 text-sage-text focus:border-sage-accent transition-colors duration-150"
              />
              <button
                type="submit"
                disabled={fundLoading}
                data-testid="fund-submit"
                className="border border-sage-accent text-sage-accent px-6 py-3 uppercase tracking-[0.1em] text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-50"
              >
                {fundLoading ? "…" : "Pitch in →"}
              </button>
            </div>
            <p className="text-[10px] text-sage-faint mt-4 uppercase tracking-[0.15em]">
              Beta: contribution intent captured, Sage follows up by hand. Automated payments wire in soon.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
